import React from 'react'
import styled, {css, keyframes} from 'styled-components';
import { useDispatch, useSelector } from 'react-redux'

import { Button, TextField, Animation } from 'components'
import { AnimatePresence, motion } from 'framer-motion'
import { FaTimesCircle } from 'react-icons/fa'

import * as actionCreators from 'store/actions'
import classes from './SectionInput.module.css'
import { Section } from 'schema'

const shakeHorizontal = keyframes`
    0%, 100% {transform: translateX(0)}
    10%, 30%, 50%, 70% {transform: translateX(-10px)}
    20%, 40%, 60% {transform: translateX(10px);}
    80% {transform: translateX(8px)}
    90% {transform: translateX(-8px)}
`
const shakeHorizontalAnimation = css`
    ${shakeHorizontal} 0.8s cubic-bezier(0.455, 0.030, 0.515, 0.955) both;
`

const Container = styled.div`
    width: inherit;
    height: ${props => !props.error && '100%'};
    display: grid;
    grid-template-rows: 1fr auto;
    grid-row-gap: 0.2rem;
    animation: ${props => props.error ? shakeHorizontalAnimation : 'none'};
    border-radius: 5px;
`

const Error = styled.div`
    font-size: 0.8rem;
    color: var(--error-color);
    margin-top: 0.2rem;
    :before {
        content: '*'
    }
`

const ButtonContainer = styled.div`
    padding: 0.5rem;
    color: var(--secondary-accent-color);
    border: 2px dashed var(--secondary-color);
    background-color: var(--primary-color);
    border-radius: 5px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    font-size: 0.9rem;
    cursor: pointer;
    height: ${props => props.height || '100%'};
    margin: ${props => props.margin};
`

const SectionInput = props => {
    const {
        section,
        index,
        pageID,
        type='BUTTON',
        base='INVISIBLE',
        id,
        styles
    } = props
    const debug = false
    const [value, setValue] = React.useState(section && section['section_name'])
    const [error, setError] = React.useState(null)
    const [renderType, setType] = React.useState(type)
    const dispatch = useDispatch()
    const inputID = useSelector(state => state.content.inputID)
    const data = useSelector(state => state.db.present.data && state.db.present.data.toJS())
    const uid = useSelector(state => state.auth.uid)
    const user = useSelector(state => state.auth.user)
    const sessionId = useSelector(state => state.content.sessionId)

    React.useEffect(() => {
        if(inputID ? inputID !== id : false){
            setType(type)
        }
    }, [inputID])

    const onKeyDown = (event) => {
        const charCode = event.keyCode
        switch (charCode) {
            case 13:
                onSubmit()
                break
            case 27:
                onCancel()
                break
        }
    }

    const onOpen = (nextType) => {
        id && dispatch(actionCreators.setInput(id))
        setType(nextType)
    }

    const onInput = (event) => {
        setValue(event.target.value)
        setError(null)
    }

    const onSubmit = () => {
        if(!value || value.length === 0){
            setError('Cannot be empty')
        } else {
            if(type === 'EDIT'){
                let updatedSection = new Section({...section})
                updatedSection['section_name'] = value
                dispatch(actionCreators.updateSection(updatedSection, data, uid, sessionId))
            } else {
                let newSection = new Section({ section_name: value, pageID: pageID || user['activePageID'] })
                dispatch(actionCreators.createSection(newSection, data, id, data['pages'][newSection['pageID']], sessionId))
            }
            onCancel()
        }
        
    }

    const onCancel = () => {
        setType(type === 'CREATE' ? null : type === 'EDIT' ? 'EDIT' : type === 'BUTTON' ? 'BUTTON' : null)
        dispatch(actionCreators.setInput(null))
        setError(null)
        setValue(null)
    }

    let content = <motion.div whileHover={{ opacity: 1 }} style={{ opacity: 0 }} transition={{ duration: 0.3 }}>
                    <div className={classes.DefaultContainer} onClick={() => onOpen('CREATE')}>
                        <div className={classes.Divider}/>
                        <div>Create section</div>
                        <div className={classes.Divider}/>
                    </div>
                </motion.div>
    if(inputID ? inputID !== id : false){
        if(base === 'VISIBLE') {
            content =
            <Animation animation='default' style={styles}>
                <ButtonContainer height={styles && styles['height']} 
                    margin={styles && styles['margin']}
                    onClick={() => onOpen('CREATE')}>
                    Create section
                </ButtonContainer>
            </Animation>
        } 
    } else {
        switch(renderType){
            case 'EDIT':
                if(inputID ? inputID !== id : false) { return }
                content =
                <Animation animation='default' >
                    <div className={classes.EditContainer}>
                        <TextField type='single' style={{fontWeight: 'bold', fontSize: '1rem'}}
                            placeholder="Edit section" focus={true}
                            value={value}
                            onChange={onInput} 
                            onKeyDown={onKeyDown}/>
                        <Button icon={<FaTimesCircle/>} tooltip='Cancel' onSelect={onCancel} 
                            styles={{ margin: 0, padding: '0.3rem', color: 'var(--secondary-accent-color)', backgroundColor: 'var(--secondary-color)', boxShadowColor: 'transparent' }}/>
                        <Button text='Edit' onSelect={onSubmit} disable={!error}
                            styles={{backgroundColor:'var(--focus-color)', color: 'white', margin:'0', fontWeight:'bolder'}}/>
                    </div>
                </Animation>
                break
            case 'CREATE':
                if(inputID ? inputID !== id : false) { return }
                content = 
                <AnimatePresence>
                    <Animation animation='default'>
                    <div className={classes.EditContainer}>
                        <TextField type='single' style={{fontWeight: 'bold', fontSize: '1rem'}}
                            placeholder="Create section" focus={true}
                            value={value}
                            onChange={onInput} 
                            onKeyDown={onKeyDown}/>
                        <Button icon={<FaTimesCircle/>} tooltip='Cancel' onSelect={onCancel} 
                            styles={{ margin: 0, padding: '0.3rem', color: 'var(--secondary-accent-color)', backgroundColor: 'var(--secondary-color)', boxShadowColor: 'transparent' }}/>
                        <Button text='Create' onSelect={onSubmit} disable={!error}
                            styles={{backgroundColor:'var(--focus-color)', color: 'white', margin:'0', fontWeight:'bolder'}}/>
                    </div>
                </Animation>
                </AnimatePresence>
                break
            case 'BUTTON':
                content =
                <Animation animation='default' style={styles}>
                    <ButtonContainer height={styles && styles['height']} 
                        margin={styles && styles['margin']}
                        onClick={() => onOpen('CREATE')}>
                        Create section
                    </ButtonContainer>
                </Animation>
                break
        }
    }
    

    return (
        <Container error={error}>
            {content}
            {error && <Error>{error}</Error>}
        </Container>
    )
}

export default SectionInput;